import React from 'react';
import { useStore } from '@/lib/store';
import { PlaybackProvider } from '@/lib/playback';
import HighlightStack from './HighlightStack';
import HighlightCards from './HighlightCards';
import HighlightTimeline from './HighlightTimeline';
import HighlightRefine from './HighlightRefine';
import { RotateCcw, Plus } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'stack',    label: 'Stack' },
  { key: 'cards',    label: 'Cards' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'refine',   label: 'Refine' },
];

export default function HighlightShowcase() {
  const { state, setState, runExtraction, clearSession } = useStore();

  // Section is entirely hidden until first successful run.
  if (!state.showcaseVisible && !state.showcaseSkeleton) return <div id="showcase" style={{ scrollMarginTop: 80 }} />;

  const run = state.currentRun;
  const activeTab = state.activeTab;

  return (
    <PlaybackProvider key={run?.runId || run?.sampleId || run?.source || 'empty'}>
    <section id="showcase" data-testid="highlight-showcase" className="py-12 sm:py-16 anim-slide-up" style={{ scrollMarginTop: 80 }}>
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">— THE SHOWCASE</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">
              One recording. <span className="text-grad">Every angle.</span>
            </h2>
            {run && (
              <div className="mt-2 font-mono text-xs text-white/45">
                {run.source} {run.version ? `· v${run.version}` : ''} · {run.duration} · {run.themesFound} themes · Top pick {run.highlights[0].rating}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn-ghost text-sm inline-flex items-center gap-1.5"
              onClick={() => {
                document.getElementById('upload')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              data-testid="new-run-btn"
            >
              <Plus className="h-4 w-4" /> New Run
            </button>
            <button
              className="btn-ghost text-sm inline-flex items-center gap-1.5"
              onClick={() => { clearSession(); toast.success('Session cleared', { description: 'Everything wiped from this browser.' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              data-testid="clear-session-btn"
            >
              <RotateCcw className="h-4 w-4" /> Clear Session
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="tab-seg mb-6" role="tablist" aria-label="Showcase views">
          {TABS.map(t => (
            <button
              key={t.key}
              role="tab"
              aria-selected={activeTab === t.key}
              className={`tab-btn ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setState({ activeTab: t.key })}
              data-testid={`showcase-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Skeleton or content */}
        {state.showcaseSkeleton ? (
          <div className="grid md:grid-cols-3 gap-5">
            {[0,1,2].map(i => (
              <div key={i} className="hl-card" style={{ height: 340 }}>
                <div className="skel h-4 w-24 mb-3" />
                <div className="skel h-6 w-3/4 mb-2" />
                <div className="skel h-4 w-full mb-1" />
                <div className="skel h-4 w-5/6 mb-6" />
                <div className="skel h-16 w-full mb-4" />
                <div className="flex justify-between">
                  <div className="skel h-8 w-8 rounded-full" />
                  <div className="skel h-8 w-20 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div role="tabpanel">
            {activeTab === 'stack'    && <HighlightStack    highlights={run.highlights} source={run.source} />}
            {activeTab === 'cards'    && <HighlightCards    highlights={run.highlights} source={run.source} />}
            {activeTab === 'timeline' && <HighlightTimeline run={run} />}
            {activeTab === 'refine'   && <HighlightRefine />}
          </div>
        )}
      </div>
    </section>
    </PlaybackProvider>
  );
}
